import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Pagination, Stack } from "@mui/material";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";
import SupplierCard from "../components/suppliers/SupplierCard";
import CreateSupplierModal from "../components/suppliers/CreateSupplierModal";
import UpdateSupplierModal from "../components/suppliers/UpdateSupplierModal";
import ViewSupplierModal from "../components/suppliers/ViewSupplierModal";
import AppCircularProgress from "../components/common/AppCircularProgress";
import PageError from "../components/common/PageError";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getSuppliers,
  getSupplierById,
  deleteSupplier,
  resetSupplierServerStatus,
} from "../redux/actions/supplierActions";
import { SupplierSummaryDto } from "../shared/dtos/supplier/SupplierSummaryDto";

const SuppliersPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    items: suppliers,
    item: selectedSupplierData,
    pagination,
    loadingFetchAll,
    errorFetchAll,
    loadingFetchOne,
    errorFetchOne,
    serverStatus,
    loadingDelete,
    loadingUpdate,
    loadingCreate,
    errorCreate,
    errorUpdate,
  } = useAppSelector((state) => state.supplier);

  const [page, setPage] = useState<number>(1);
  const limit = 12;
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const canCreate = hasPermission("supplier:create");
  const canUpdate = hasPermission("supplier:update");
  const canDelete = hasPermission("supplier:delete");

  const fetchSuppliersData = useCallback((): void => {
    dispatch(getSuppliers({ page, size: limit, search: debouncedSearch }));
  }, [dispatch, page, limit, debouncedSearch]);

  useEffect(() => {
    fetchSuppliersData();
  }, [fetchSuppliersData]);
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback((): void => {
    if (!loadingCreate) setIsCreateModalOpen(false);
  }, [loadingCreate]);

  const handleOpenView = (id: string): void => {
    dispatch(getSupplierById(id));
    setIsViewModalOpen(true);
  };

  const handleOpenUpdate = (id: string): void => {
    dispatch(getSupplierById(id));
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback((): void => {
    if (!loadingUpdate) setIsUpdateModalOpen(false);
  }, [loadingUpdate]);

  const handleOpenDelete = (id: string): void => {
    setSelectedId(id);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback((): void => {
    if (!loadingDelete) {
      setIsDeleteModalOpen(false);
      setSelectedId(null);
    }
  }, [loadingDelete]);

  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;
    if (operation === "create" && (status === 201 || status === 200))
      handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();
    dispatch(resetSupplierServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  const renderContent = (): React.JSX.Element => {
    if (loadingFetchAll) return <AppCircularProgress />;
    if (errorFetchAll)
      return <PageError error={errorFetchAll} onRetry={fetchSuppliersData} />;
    if (!suppliers || suppliers.length === 0)
      return (
        <Typography sx={{ mt: 5, textAlign: "center" }}>
          No suppliers found.
        </Typography>
      );

    return (
      <>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: 3,
          }}
        >
          {suppliers.map((supplier: SupplierSummaryDto) => (
            <Box
              key={supplier.id}
              sx={{ cursor: "pointer" }}
              onClick={() => handleOpenView(supplier.id)}
            >
              <SupplierCard
                supplier={supplier as unknown as SupplierSummaryDto}
                onView={(e) => {
                  e.stopPropagation();
                  handleOpenView(supplier.id);
                }}
                onEdit={
                  canUpdate
                    ? (e) => {
                        e.stopPropagation();
                        handleOpenUpdate(supplier.id);
                      }
                    : undefined
                }
                onDelete={
                  canDelete
                    ? (e) => {
                        e.stopPropagation();
                        handleOpenDelete(supplier.id);
                      }
                    : undefined
                }
              />
            </Box>
          ))}
        </Box>
        {pagination?.totalPages && pagination.totalPages > 1 && (
          <Stack sx={{ alignItems: "center", mt: 4 }}>
            <Pagination
              count={pagination.totalPages}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
            />
          </Stack>
        )}
      </>
    );
  };

  return (
    <PageContainer
      title="Supplier Management"
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddBusinessIcon />}
            onClick={handleOpenCreate}
          >
            Add Supplier
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search by name, code or PIN..."
      />
      {renderContent()}

      {isCreateModalOpen && (
        <CreateSupplierModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingCreate}
          error={errorCreate}
        />
      )}
      {isUpdateModalOpen && selectedSupplierData && (
        <UpdateSupplierModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          supplier={selectedSupplierData}
          loading={loadingUpdate}
          error={errorUpdate}
        />
      )}
      {isViewModalOpen && (
        <ViewSupplierModal
          open={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          supplier={selectedSupplierData}
          loading={loadingFetchOne}
          error={errorFetchOne}
        />
      )}

      {isDeleteModalOpen && selectedId && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={() => dispatch(deleteSupplier(selectedId))}
          title="Delete Supplier"
          description="Confirm removal of this supplier and all associated contact records?"
          //loading={loadingDelete}
        />
      )}
    </PageContainer>
  );
};
export default SuppliersPage;
